import axiosRetry from 'axios-retry';
import { appInsights } from '../shared/helpers/appInsights';
import { generateUID } from "../shared/helpers/GenerateUniqueId";
import { AccountEndPointsClient, AlertEndPointsClient, AuthEndPointsClient, AuthorizationEndPointsClient, CernerEndPointsClient, ESAFEndPointsClient, FacilityEndPointsClient, NoteEndPointsClient, PatientInstructionsEndpointsClient, PreRegEndpointsClient, UserEndPointsClient, UserInfo, WorkFlowEndPointsClient, WorkQueueEndPointsClient } from "./generated";
import axios from "axios";
import React from 'react';
import { refreshAccessToken } from './AuthenticationService';
import { fetchUserInfo } from './UserService';
import { ActionType } from '../shared/interfaces/Action';
import { shouldRetryAxiosRequest } from '../shared/hooks/utils';
import {
    showStandardApiError,
    trackApiRequestEnd,
    trackApiRequestFailure,
    trackApiRequestStart
} from './api-feedback';

declare module 'axios' {
    export interface InternalAxiosRequestConfig {
        __apiFeedbackTracked?: boolean;
        __apiFeedbackCompleted?: boolean;
    }
}

const baseURL = process.env.REACT_APP_BASE_URL;

export const axiosInstance = axios.create({
	baseURL,
});

axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: (retryCount) => {
		return 2000; //Delay for 2 Sec
	  },
    retryCondition: shouldRetryAxiosRequest,
});

const responseHandler = (response: any) => {
    if (response?.config?.__apiFeedbackTracked && !response?.config?.__apiFeedbackCompleted) {
        response.config.__apiFeedbackCompleted = true;
        trackApiRequestEnd();
    }

    if (response?.data && typeof response.data === 'object' && 'isSuccess' in response.data && response.data.isSuccess === false) {
        showStandardApiError();
    }

    return response;
};

const errorHandler = (error: any) => {
    if (error?.config?.__apiFeedbackTracked && !error?.config?.__apiFeedbackCompleted && !error.config.url.includes('account/searchAccount')) {
        error.config.__apiFeedbackCompleted = true;
        trackApiRequestFailure();
    }

    // Logging the exception into appinsights
    var data = "";
    if (error.config.data !== undefined && error.config.data !== '') {
        data = JSON.parse(error.config.data);
    }

    var detailedErrorMessage = "";
    if (error.request.responseText !== undefined && error.request.responseText !== '') {
        detailedErrorMessage = error.request.responseText;
    }

    // var bearerheader = "";
	// if (error.config.headers !== undefined && error.config.headers !== '') {
	// 	bearerheader = error.config.headers.Authorization;
	// }

    var requestUniqueId = "";
	if (error.config.headers !== undefined && error.config.headers !== '') {
		requestUniqueId = error.config.headers.RequestId;
	}

    let global: any = globalThis;
    let userDetails: UserInfo = Object.freeze(global.userDetails);
    const properties = {
        userId : userDetails?.userId,
        requestId : requestUniqueId,
        application : "TECToolUI",
        apiUrl:error.config.url,
        apiPayload:data,
        errorCode : error.code,
        errorMessage:error.message,
        errorText:detailedErrorMessage
    }
    appInsights.trackException({ error: error, properties });

    if(error.code === 'ERR_NETWORK') // Token expiry/unauthoirized with apigee
    {
        console.log("error with access token : /unauthorized/expired token/token missing");
        //navigate to home page for refreshing token 
        const originURL = window.location.origin;
        window.location.href = originURL;
    }
    return Promise.reject(error);
};

// Add a unique Id for each request to correlate API error in backend
// Add bearer token 
axiosInstance.interceptors.request.use(
    async config => {
        if (!config.__apiFeedbackTracked) {
            config.__apiFeedbackTracked = true;
            config.__apiFeedbackCompleted = false;
            trackApiRequestStart();
        }

        if (new Date(localStorage.getItem("accesstoken_expiry")!) <= new Date()) {
            // renew access token 
            const data = await refreshAccessToken();
            // handle ping server error on refresh token expiry
            if (data.type === ActionType.FETCH_ACCESS_TOKEN_FAILURE) {
                console.log("refresh token failed!!");
                localStorage.clear();
                const originURL = window.location.origin;
                window.location.href = originURL;
            }

            // Update UserInfo & TECToolAuthorizeToken
            const userData: any = await fetchUserInfo();
            // update global user info 
            if (userData !== null) {
                let global: any = globalThis;
                global.userDetails = userData.payload;
            }
        }

      const token = localStorage.getItem("access_token");  // token
      config.headers['Authorization'] =  'Bearer ' + token;

      config.headers['RequestId'] = generateUID();  // unique id

      return config;

      },
      error => {
          return Promise.reject(error);
      }
  );

axiosInstance.interceptors.response.use(
	(response) => responseHandler(response),
	(error) => errorHandler(error)
);


export interface IApi {
    account: AccountEndPointsClient;
    authentication: AuthEndPointsClient;
    esaf: ESAFEndPointsClient;
    facility: FacilityEndPointsClient;
    note: NoteEndPointsClient;
    workQueue: WorkQueueEndPointsClient;
    authorization: AuthorizationEndPointsClient;
    user: UserEndPointsClient;
    alert: AlertEndPointsClient;
    workflow: WorkFlowEndPointsClient;
    preReg: PreRegEndpointsClient;
    patientInstructions: PatientInstructionsEndpointsClient,
    cerner: CernerEndPointsClient
}

const api: IApi = {
    account:  new AccountEndPointsClient(undefined, axiosInstance),
    authentication: new AuthEndPointsClient(undefined, axiosInstance),
    esaf: new ESAFEndPointsClient(undefined, axiosInstance),
    facility: new FacilityEndPointsClient(undefined, axiosInstance),
    note: new NoteEndPointsClient(undefined, axiosInstance),
    workQueue : new WorkQueueEndPointsClient(undefined, axiosInstance),
    authorization : new AuthorizationEndPointsClient(undefined,axiosInstance),
    workflow: new WorkFlowEndPointsClient(undefined,axiosInstance),
    user: new UserEndPointsClient(undefined,axiosInstance),
    alert: new AlertEndPointsClient(undefined,axiosInstance),
    preReg: new PreRegEndpointsClient(undefined,axiosInstance),
    patientInstructions: new PatientInstructionsEndpointsClient(undefined,axiosInstance),
    cerner: new CernerEndPointsClient(undefined,axiosInstance)
}

export const ApiContext = React.createContext<IApi>({} as IApi);
export default api; 
